import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, ToggleButtonGroup, ToggleButton, Tooltip, Typography } from "@mui/material";
import styles from "../styles";
import Popover from '@mui/material/Popover';
import Chrome from 'react-color';
import update from 'immutability-helper';
import Paywall from "./Paywall";
import { CompactPicker } from "react-color";
import SettingsIcon from '@mui/icons-material/Settings';
import CropLandscapeIcon from '@mui/icons-material/CropLandscape';
import CropPortraitIcon from '@mui/icons-material/CropPortrait';
import CreateAccountDialog from "./dialogs/CreateAccoutDialog";

/**
 * Component for managing the settings of a fretboard.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.setFretboardTitle - Function to set the fretboard title.
 * @param {Function} props.setFretboardStringCount - Function to set the number of strings on the fretboard.
 * @param {Function} props.setFretboard - Function to set the fretboard settings.
 * @param {Object} props.Fretboard - The current fretboard settings.
 * @param {boolean} props.premium - Flag indicating if the user has premium access.
 * @param {Function} props.setIsPremium - Function to set the premium access status.
 * @param {Function} props.handleUpgrade - Function to handle the upgrade process.
 * @param {boolean} props.showFretboardModal - Flag indicating if the fretboard settings modal is visible.
 * @param {Function} props.setShowFretboardModal - Function to set the visibility of the fretboard settings modal.
 * @param {boolean} props.centerMarkers - Flag indicating if the markers should be centered.
 * @param {Function} props.setCenterMarkers - Function to set the centering of the markers.
 * @param {boolean} props.isAnon - Flag indicating if the user is anonymous.
 * @returns {JSX.Element} The rendered FretboardSettings component.
 */
const FretboardSettings = ({
  setFretboardTitle,
  setFretboardStringCount,
  setFretboard,
  Fretboard,
  premium,
  setIsPremium,
  handleUpgrade,
  showCode,
  disabled,
  showFretboardModal,
  setShowFretboardModal,
  centerMarkers,
  setCenterMarkers,
  isAnon
}) => {
  const [open, setOpen] = useState(false);
  const [openUpgrade, setOpenUpgrade] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [startFret, setStartFret] = useState(Fretboard.start_fret || 1);
  const [endFret, setEndFret] = useState(Fretboard.end_fret || 23);
  const [background_color, set_background_color] = useState(Fretboard.background_color);
  const [openBackground, setOpenBackground] = useState(false);
  const [nut_color, set_nut_color] = useState(Fretboard.nut_color);
  const [color, set_foreground_color] = useState(Fretboard.color);
  const [openForeground, setOpenForegroundColor] = useState(false);
  const [marker_color, set_marker_color] = useState(Fretboard.marker_color);
  const [nut_font_color, set_nut_font_color] = useState(Fretboard.nut_font_color);
  const [openNutColor, setOpenNutColor] = useState(false);
  const [openNutFontColor, setOpenNutFontColor] = useState(false);
  const [openMarkerColor, setOpenMarkerColor] = useState(false);
  const [orientation, _setOrientation] = useState(Fretboard.orientation ? Fretboard.orientation : "landscape");
  const [width, _setWidth] = useState(Fretboard.width ? Fretboard.width : 750);
  const [markerCenter, setMarkerCenter] = useState(true); // eslint-disable-line no-unused-vars
  const [_stringCount, _setStringCount] = useState(6); // eslint-disable-line no-unused-vars
  const [_title, _setTitle] = useState("");
  const [showCreateAccount, setShowCreateAccount] = useState(false);

  const orientationRef = useRef(orientation);
  const widthRef = useRef(width);

  const setWidth = (data) => { widthRef.current = data; _setWidth(data); };
  const setOrientation = (data) => { orientationRef.current = data; _setOrientation(data); };

  const DotWithLineIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Longer Horizontal line */}
      <line x1="1" y1="12" x2="23" y2="12" />
      {/* Filled Dot */}
      <circle cx="12" cy="12" r="5" fill="currentColor" />
    </svg>
  );

  const CrossedDotIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Horizontal line */}
      <line x1="1" y1="12" x2="23" y2="12" />
      {/* Vertical line */}
      <line x1="12" y1="1" x2="12" y2="23" />
      {/* Filled Dot */}
      <circle cx="12" cy="12" r="5" fill="currentColor" />
    </svg>
  );

  const ShortGuitarFretboardIcon = () => (
    // Inline SVG for short fretboard
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="120"
      height="24"
      viewBox="0 0 120 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      {/* Strings */}
      {/* ... */}
      {/* Frets */}
      {/* ... */}
    </svg>
  );

  const LongGuitarFretboardIcon = () => (
    // Inline SVG for long fretboard
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="180"
      height="24"
      viewBox="0 0 180 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      {/* Strings */}
      {/* ... */}
      {/* Frets */}
      {/* ... */}
    </svg>
  );

  const handleOpen = () => {
    if (!open) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleUpgradeClose = () => {
    setOpenUpgrade(false);
    handleUpgrade();
  };

  useEffect(() => {
    setMarkerCenter(centerMarkers);
  }, [centerMarkers]);


  useEffect(() => {
    setOpen(showFretboardModal);
  }, [showFretboardModal]);

  useEffect(() => {
    if (Fretboard && Object.keys(Fretboard).length > 0) {
      set_background_color(Fretboard.background_color);
      set_nut_color(Fretboard.nut_color);
      set_nut_font_color(Fretboard.nut_font_color);
      set_foreground_color(Fretboard.color);
      set_marker_color(Fretboard.marker_color);
      setMarkerCenter(centerMarkers);
    }
  }, [Fretboard]);


  const handleMarkerCenter = () => {
    setMarkerCenter(!markerCenter);
  };

  const handleClose = async (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    setFretboardStringCount(_stringCount);
    setFretboardTitle(_title);
    const newFretboard = update(Fretboard, {
      background_color: { $set: background_color },
      color: { $set: color },
      nut_color: { $set: nut_color },
      nut_font_color: { $set: nut_font_color },
      marker_color: { $set: marker_color },
      start_fret: { $set: startFret },
      end_fret: { $set: endFret },
      total_frets: { $set: endFret - startFret + 1 },
      width: { $set: widthRef.current },
      orientation: { $set: orientationRef.current },
      centerMarkers: { $set: markerCenter },
    });
    setFretboard(newFretboard);
    setOpen(false);
    setShowFretboardModal(false);
    setCenterMarkers(markerCenter);
  };

  const handleChange = (event) => {
    _setStringCount(event.target.value);
  };

  const handleStartFretChange = (event) => {
    const newStartFret = event.target.value;
    setStartFret(newStartFret);
    if (endFret <= newStartFret) {
      setEndFret(newStartFret + 1);
    }
  };

  const handleEndFretChange = (event) => {
    const newEndFret = event.target.value;
    if (newEndFret <= startFret) {
      setEndFret(startFret + 1);
    } else {
      setEndFret(newEndFret);
    }
  };

  const handleTitleChange = (event) => {
    _setTitle(event.target.value);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleWidthChange = (event, newWidth) => {
    if (newWidth === null) return;
    setWidth(newWidth);
    if (newWidth === 250) {
      setEndFret(startFret + 7);
    }
  };

  const handleOrientationChange = (event, newOrientation) => {
    if (newOrientation === null) return;
    setOrientation(newOrientation);
  };

  const handleBackgroundColorClose = () => {
    setOpenBackground(false);
  };

  const handleColorButtonClick = (event, type) => {
    setAnchorEl(event.currentTarget);
    if (!premium) {
      setOpenUpgrade(true);
      setShowCreateAccount(true);
      return;
    }
    switch (type) {
      case 'background':
        setOpenBackground(true);
        break;
      case 'nut':
        setOpenNutColor(true);
        break;
      case 'font':
        setOpenNutFontColor(true);
        break;
      case 'marker':
        setOpenMarkerColor(true);
        break;
      case "foreground":
        setOpenForegroundColor(true);
        break;
      default:
        break;
    }

  };



  return (
    <Box>
      <Box display="flex" flexDirection="row">
        <Button
          id="fretboard-settings-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleOpen}
          variant="outlined"
          size='small'
          sx={{ marginRight: '5px' }}
          endIcon={<SettingsIcon />}
          disabled={disabled}
        >
          Fretboard
        </Button>
        <Dialog
          id="fretboard-settings-modal"
          open={open}
          onClose={handleClose}
          maxWidth="md"
          fullWidth="true"
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"

        >
          <DialogTitle>Fretboard Settings</DialogTitle>
          <Box sx={{ padding: '1rem' }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Strings</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={_stringCount}
                label="Strings"
                onChange={handleChange}
                disabled={showFretboardModal}
              >
                <MenuItem value={4}>Four</MenuItem>
                <MenuItem value={5}>Five</MenuItem>
                <MenuItem value={6}>Six</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ padding: '1rem' }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Start Fret</InputLabel>
              <Select
                labelId="start-fret-select-label"
                id="start-fret-select"
                value={startFret}
                label="Start Fret Number"
                onChange={handleStartFretChange}
                disabled={showFretboardModal}
              >
                {Array.from({ length: 20 }, (_, i) => i + 1).map((number) => (
                  <MenuItem key={number} value={parseInt(number)}>{number}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ padding: '1rem' }}>
            <FormControl fullWidth >
              <InputLabel id="demo-simple-select-label">End Fret</InputLabel>
              <Select
                labelId="end-fret-select-label"
                id="end-fret-select"
                value={endFret}
                label="End Fret Number"
                onChange={handleEndFretChange}
                disabled={showFretboardModal}
              >
                {Array.from({ length: 21 }, (_, i) => i + 3).map((number) => (
                  <MenuItem key={number} value={number} disabled={number <= startFret}>{number}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Width */}
          <Box sx={{ padding: '1rem' }}>
            <InputLabel id="fretboard_width">Width</InputLabel>
            <FormControl fullWidth>
              <ToggleButtonGroup
                value={width}
                exclusive
                onChange={handleWidthChange}
                aria-label="width"
                color="primary"
                sx={{ width: '100%' }}
                disabled={showFretboardModal}
              >
                <ToggleButton value={250} aria-label="short">
                  short
                </ToggleButton>
                <ToggleButton value={750} aria-label="long">
                  long
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </Box>

          {/* Orientation */}
          <Box sx={{ padding: '1rem' }}>
            <InputLabel id="fretboard_orientation">Fretboard Orientation</InputLabel>
            <FormControl fullWidth>
              <ToggleButtonGroup
                value={orientation}
                exclusive
                onChange={handleOrientationChange}
                aria-label="orientation"
                color="primary"
                sx={{ width: '100%' }}
                disabled={showFretboardModal}
              >
                <ToggleButton value="portrait" aria-label="portrait">
                  <CropPortraitIcon />
                </ToggleButton>
                <ToggleButton value="landscape" aria-label="landscape">
                  <CropLandscapeIcon />
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          </Box>

          {/* Markers */}
          <Box sx={{ padding: '1rem' }}>
            <InputLabel id="fretboard_orientation">Marker Placement</InputLabel>
            <FormControl fullWidth>
              <ToggleButtonGroup
                value={markerCenter}
                exclusive
                onChange={() => handleMarkerCenter()}
                aria-label="marker location"
                color="primary"
                sx={{ width: '100%' }}
              >
                <ToggleButton value={true} aria-label="marker centered">
                  <DotWithLineIcon />
                </ToggleButton>


                <ToggleButton value={false} aria-label="marker on fret">
                  <CrossedDotIcon />
                </ToggleButton>

              </ToggleButtonGroup>
            </FormControl>
          </Box>
          <Box sx={{ padding: '1rem' }}>
            <InputLabel id="fretboard_orientation">Colors</InputLabel>
            <FormControl fullWidth>
              <Stack spacing={2} direction="row">
                {/* Fretboard Color */}
                <Button onClick={(event) => handleColorButtonClick(event, "background")} sx={{ backgroundColor: background_color, color: color, border: '1px solid black' }}>Fretboard</Button>
                <Popover
                  open={openBackground}
                  anchorEl={anchorEl}
                  onClose={() => setOpenBackground(false)}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Box sx={styles.popover}>
                    <CompactPicker colors={['#4D4D4D', '#999999', '#FFFFFF', '#F44E3B', '#FE9200', '#FCDC00', '#DBDF00', '#A4DD00', '#68CCCA', '#73D8FF', '#AEA1FF', '#FDA1FF', '#333333', '#808080', '#cccccc', '#D33115', '#E27300', '#FCC400', '#B0BC00', '#68BC00', '#16A5A5', '#009CE0', '#7B64FF', '#FA28FF', '#000000', '#666666', '#B3B3B3', '#9F0500', '#C45100', '#FB9E00', '#808900', '#194D33', '#0C797D', '#0062B1', '#653294', '#AB149E', 'transparent']} color={background_color} onChange={(color) => set_background_color(color.hex)} />
                  </Box>
                </Popover>

                {/* Fretboard Foreground Color */}
                <Button onClick={(event) => handleColorButtonClick(event, "foreground")} sx={{ backgroundColor: color, color: background_color, border: '1px solid black' }}>Foreground</Button>
                <Popover
                  open={openForeground}
                  anchorEl={anchorEl}
                  onClose={() => setOpenForegroundColor(false)}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Box sx={styles.popover}>
                    <Chrome color={color} onChange={(color) => set_foreground_color(color.hex)} />
                  </Box>
                </Popover>
                {/* Nut Color */}
                <Button onClick={(event) => handleColorButtonClick(event, "nut")} sx={{ backgroundColor: nut_color, color: nut_font_color, border: '1px solid black' }}>Nut</Button>
                <Popover

                  open={openNutColor}
                  anchorEl={anchorEl}
                  onClose={() => setOpenNutColor(false)}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Box sx={styles.popover}>
                    <CompactPicker color={nut_color} onChange={(color) => set_nut_color(color.hex)} />
                  </Box>
                </Popover>
                {/* Nut Font Color */}
                <Button onClick={(event) => handleColorButtonClick(event, "font")} sx={{ backgroundColor: nut_font_color, color: nut_color, border: '1px solid black' }}>Nut Font</Button>
                <Popover

                  open={openNutFontColor}
                  anchorEl={anchorEl}
                  onClose={() => setOpenNutFontColor(false)}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Box sx={styles.popover}>
                    <Chrome color={nut_font_color} onChange={(color) => set_nut_font_color(color.hex)} />
                  </Box>
                </Popover>
                {/* Marker Color */}
                <Button onClick={(event) => handleColorButtonClick(event, "marker")} sx={{ backgroundColor: marker_color, color: background_color, border: '1px solid black' }}>Marker</Button>
                <Popover

                  open={openMarkerColor}
                  anchorEl={anchorEl}
                  onClose={() => setOpenMarkerColor(false)}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <Box sx={styles.popover}>
                    <Chrome color={marker_color} onChange={(color) => set_marker_color(color.hex)} />
                  </Box>
                </Popover>
              </Stack>
            </FormControl>
          </Box>
          {showFretboardModal && <Typography variant="h6" gutterBottom style={{ textAlign: 'center' }}>You need to save the changes before you begin</Typography>}
          <DialogActions>
            <Button variant="contained" onClick={handleClose}>
              Save
            </Button>
            <Button variant="contained" onClick={handleCancel} disabled={showFretboardModal}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Box >

      <Paywall open={openUpgrade} setIsPremium={setIsPremium} onClose={handleUpgradeClose} isAnon={isAnon} /> {/* Render the Paywall component */}
      <CreateAccountDialog showCreateAccount={showCreateAccount} setShowCreateAccount={setShowCreateAccount} />
    </Box>
  );
};

export default FretboardSettings;
